import { DynamicForm } from "@shared/components/DynamicForm";
import { StepHelpers } from "@shared/components/Stepper";
import { cn } from "@shared/lib/utils";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Separator } from "@shared/ui/separator";
import { Search } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useBillData,
  useBillTemplates,
  useConsumers,
  useSubmitConsumerSelection,
  useUpdateConsumerSelection,
} from "../../hooks";
import { Consumer } from "../../types";
import StatusBadge from "./StatusBadge";
import { useDebounce } from "@shared/hooks/useDebounce";
interface TemplateOption {
  value: number;
  label: string;
  source: string;
  type?: string;
}

interface Step1ConsumerSelectionProps {
  // Props automatically passed by Stepper
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

const Step1ConsumerSelection: React.FC<Step1ConsumerSelectionProps> = ({
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
}) => {
  const { id, action = "add" } = useParams();
  const remoteUtilityId = useRemoteUtilityId();
  const currency = useCurrency();
  const navigate = useNavigate();
  const { data: billTemplates } = useBillTemplates();
  const { mutateAsync: submitConsumer, isPending: isSubmitting } =
    useSubmitConsumerSelection();
  const { mutateAsync: updateConsumer } = useUpdateConsumerSelection(
    stepHelpers.getStepData(0)?.billDraftId || id
  );

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 2000);

  const [selectedConsumer, setSelectedConsumer] = useState<Consumer | null>(
    null
  );
  const [shouldAutoSelectConsumer, setShouldAutoSelectConsumer] =
    useState(false);

  const dataLoadedRef = useRef(false);

  const { data: consumers, error, refetch } = useConsumers(debouncedSearchQuery);

  const { data: billData } = useBillData(id);

  // Helper function to save step data using stepHelpers
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
        console.log("Step 1 data saved:", stepData);
      } catch (error) {
        console.error("Failed to save step 1 data:", error);
      }
    },
    [stepHelpers, currentStepIndex]
  );

  // Helper function to get step data using stepHelpers
  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step 1 data:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Define form fields
  const searchFilterFormFields: FormField[] = [
    {
      name: "startDate",
      label: "Billing Period Start",
      type: "datepicker",
      required: true,
      placeholder: "Select start date",
      fullWidth: true,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "endDate",
      label: "Billing Period End",
      type: "datepicker",
      required: true,
      placeholder: "Select end date",
      fullWidth: true,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "billTemplate",
      label: "Bill Template",
      type: "select",
      required: true,
      placeholder: "Select a template",
      fullWidth: true,
      options: billTemplates?.map((template) => ({
        value: JSON.stringify(template),
        label: template.label,
      })),
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none bg-white",
        error: "text-red-600 text-sm mt-1",
      },
    },
  ];

  // Create form instance
  const searchFilterForm = FormService.createForm({
    fields: searchFilterFormFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Load saved data on component mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        console.log("savedStepData", savedStepData);
        console.log("action billData", action, billData);

        if (savedStepData) {
          // Restore form data
          if (savedStepData.formData) {
            const formData = {
              ...(savedStepData.formData.startDate !== "" && {
                startDate: savedStepData.formData.startDate,
              }),
              ...(savedStepData.formData.endDate !== "" && {
                endDate: savedStepData.formData.endDate,
              }),
              billTemplate: savedStepData.formData.billTemplate,
            };
            console.log("Loaded step 1 data:", savedStepData);
            FormService.populateForm(
              searchFilterForm,
              formData,
              searchFilterFormFields
            );
          }

          // Restore selected consumer
          if (savedStepData.selectedConsumer) {
            setSelectedConsumer(savedStepData.selectedConsumer);
          }

          // Restore search query
          if (savedStepData.searchQuery) {
            setSearchQuery(savedStepData.searchQuery);
          }

          // Store billId from URL params if this is edit mode and we don't have it stored yet
          if (id && !savedStepData.billId) {
            saveStepData({
              ...savedStepData,
              billId: id,
              action: action,
              isEditMode: action === "edit",
            });
          }

          dataLoadedRef.current = true;
        } else if (action === "draft" && billData) {
          const formData = {
            startDate: billData.invoiceStartDate,
            endDate: billData.invoiceEndDate,
            billTemplate: JSON.stringify({
              value: billData.billConfigId,
              label: billData.billConfigName,
              source: billData.billConfigType,
            }),
          };
          FormService.populateForm(
            searchFilterForm,
            formData,
            searchFilterFormFields
          );

          setSearchQuery(billData.consumerData.consumerNo);
          setShouldAutoSelectConsumer(true);

          dataLoadedRef.current = true;
        }
        //  else if (savedStepData) {
        //   // Initial load for edit mode - store billId from URL
        //   saveStepData({
        //     formData: searchFilterForm.getValues(),
        //   });
        // }
      } catch (error) {
        console.error("Failed to load step 1 data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [
    stepHelpers,
    currentStepIndex,
    id,
    action,
    getStepData,
    saveStepData,
    searchFilterForm,
    billData,
  ]);

  const handleFieldChange = (fieldName: string, value: any) => {
    const currentData = getStepData() || {};
    const updatedData = {
      ...currentData,
      [fieldName]: value,
      lastUpdated: new Date().toISOString(),
    };
    saveStepData(updatedData);
  };

  // Handle consumer selection
  const handleSelectConsumer = useCallback(
    (consumer: Consumer) => {
      setSelectedConsumer(consumer);

      // Save selected consumer data
      saveStepData({
        selectedConsumer: consumer,
        searchQuery: searchQuery,
        formData: searchFilterForm.getValues(),
        consumerSelected: true,
        consumerData: {
          id: consumer.id,
          name: consumer.name,
          accountNo: consumer.accountNo,
          premiseId: consumer.premiseId,
          status: consumer.status,
          utilities: consumer.utilities,
          totalOutstanding: consumer.totalOutstanding,
          lastBillDate: consumer.lastBillDate,
          activateDate: consumer.activateDate,
        },
      });
    },
    [saveStepData, searchQuery, searchFilterForm]
  );

  useEffect(() => {
    console.log("consumers", consumers);

    if (shouldAutoSelectConsumer && consumers && consumers.length > 0) {
      console.log("consumers", consumers, consumers[0]);
      // Select the first consumer (index 0)
      const targetConsumer = consumers[0];
      handleSelectConsumer(targetConsumer);

      // Reset the flag
      setShouldAutoSelectConsumer(false);

      console.log("Auto-selected consumer:", targetConsumer);
    }
  }, [consumers, shouldAutoSelectConsumer, handleSelectConsumer]);

  // Handle search query change
 const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);

      // Save search query
      const currentData = getStepData() || {};
      saveStepData({
        ...currentData,
        searchQuery: value,
      });
    },
    [getStepData, saveStepData]
  );

  // Handle form field changes
  // const handleFieldChange = useCallback(
  //   (fieldName: string, value: any) => {
  //     const currentData = getStepData() || {};
  //     const currentFormData = searchFilterForm.getValues();

  //     saveStepData({
  //       ...currentData,
  //       formData: {
  //         ...currentFormData,
  //         [fieldName]: value,
  //       },
  //       lastUpdated: new Date().toISOString(),
  //     });
  //   },
  //   [getStepData, saveStepData, searchFilterForm]
  // );


  
  // Handle continue button
  const handleContinue = async () => {
    // Clear any previous validation errors
    if (clearValidationError) {
      clearValidationError();
    }

    // Validate form
    const isValid = await FormService.validateForm(searchFilterForm);
    if (!isValid) {
      if (setValidationError) {
        setValidationError("Please fill in all required fields.");
      }
      return;
    }

    // Validate consumer selection
    if (!selectedConsumer) {
      if (setValidationError) {
        setValidationError("Please select a consumer to continue.");
      }
      return;
    }

    try {
      const payload = searchFilterForm.getValues();

      // Convert billTemplate from string to object
      const rawTemplate = payload.billTemplate;
      const billtemplateData =
        typeof rawTemplate === "string" ? JSON.parse(rawTemplate) : rawTemplate;

      // Build bill_data
      payload.bill_data = {
        remote_utility_id: remoteUtilityId,
        remote_consumer_number: selectedConsumer.name,
        invoice_start_date: payload.startDate,
        invoice_end_date: payload.endDate,
        bill_config_id: billtemplateData.value,
        bill_config_type: billtemplateData.source,
        bill_config_name: billtemplateData.label,
        consumer_data: {
          id: selectedConsumer.id,
          consumer_no: selectedConsumer.accountNo,
          status: selectedConsumer.status,
        },
        utility_service: selectedConsumer.utilities.map((u) => u),
      };

      payload.step = {
        step_working_on: 0,
        total_steps: 3,
      };

      // Remove temp form fields
      delete payload.startDate;
      delete payload.endDate;
      delete payload.billTemplate;

      let response;
      let billDraftId;

      if (stepHelpers.getStepData(0)?.billDraftId || id) {
        response = await updateConsumer(payload);
        billDraftId = response?.billDraftId;
      } else {
        response = await submitConsumer(payload);
        billDraftId = response?.billDraftId;
      }

      if (billDraftId) {
        // Save final step data with billDraftId
        saveStepData({
          selectedConsumer,
          formData: searchFilterForm.getValues(),
          billtemplateData,
          payload,
          billDraftId,
          response,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        });

        // Proceed to next step
        if (onNext) {
          onNext();
        }
      } else {
        if (setValidationError) {
          setValidationError("Failed to create bill draft. Please try again.");
        }
      }
    } catch (error) {
      console.error("Failed to submit consumer selection:", error);
      if (setValidationError) {
        setValidationError("Failed to submit. Please try again.");
      }
    }
  };

  const handleDraft = async () => {
    // Clear any previous validation errors
    if (clearValidationError) {
      clearValidationError();
    }

    // Validate form
    const isValid = await FormService.validateForm(searchFilterForm);
    if (!isValid) {
      if (setValidationError) {
        setValidationError("Please fill in all required fields.");
      }
      return;
    }

    // Validate consumer selection
    if (!selectedConsumer) {
      if (setValidationError) {
        setValidationError("Please select a consumer to continue.");
      }
      return;
    }

    try {
      const payload = searchFilterForm.getValues();

      // Convert billTemplate from string to object
      const rawTemplate = payload.billTemplate;
      const billtemplateData =
        typeof rawTemplate === "string" ? JSON.parse(rawTemplate) : rawTemplate;

      // Build bill_data
      payload.bill_data = {
        remote_utility_id: remoteUtilityId,
        remote_consumer_number: selectedConsumer.name,
        invoice_start_date: payload.startDate,
        invoice_end_date: payload.endDate,
        bill_config_id: billtemplateData.value,
        bill_config_type: billtemplateData.source,
        bill_config_name: billtemplateData.label,
        consumer_data: {
          id: selectedConsumer.id,
          consumer_no: selectedConsumer.accountNo,
          status: selectedConsumer.status,
        },
        utility_service: selectedConsumer.utilities.map((u) => u),
      };

      payload.step = {
        step_working_on: 0,
        total_steps: 3,
      };

      // Remove temp form fields
      delete payload.startDate;
      delete payload.endDate;
      delete payload.billTemplate;

      let response;
      let billDraftId;

      if (stepHelpers.getStepData(0)?.billDraftId || id) {
        response = await updateConsumer(payload);
        billDraftId = response?.billDraftId;
      } else {
        response = await submitConsumer(payload);
        billDraftId = response?.billDraftId;
      }

      if (billDraftId) {
        // Save final step data with billDraftId
        saveStepData({
          selectedConsumer,
          formData: searchFilterForm.getValues(),
          billtemplateData,
          payload,
          billDraftId,
          response,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        });

        navigate("/bx/history?tab=single&tab1=draft");
      } else {
        if (setValidationError) {
          setValidationError("Failed to create bill draft. Please try again.");
        }
      }
    } catch (error) {
      console.error("Failed to submit consumer selection:", error);
      if (setValidationError) {
        setValidationError("Failed to submit. Please try again.");
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="card-subheading-text">
          Select a consumer and configure billing details to start the bill
          generation process.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      
         <Input
      placeholder="Search by name, account no"
      value={searchQuery} 
      onChange={(e) => handleSearchChange(e.target.value)}
      className="pl-9"
    />
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Search and filter panel */}
        <div className="md:col-span-1 space-y-4">
          <Card>
            <CardContent className="">
              <div className="w-full max-w-md">
                <div className="space-y-4">
                
                  <DynamicForm
                    fields={searchFilterFormFields}
                    form={searchFilterForm}
                    onFieldChange={handleFieldChange}
                    config={{
                      layout: "vertical",
                      spacing: "small",
                      showResetButton: false,
                      showSubmitButton: false,
                      resetButtonText: "Clear Filters",
                      submitButtonText: "Apply Filters",
                      classes: {
                        form: "w-full space-y-4",
                        fieldContainer: "w-full",
                        buttonContainer:
                          "flex justify-start gap-2 mt-6 pt-4 border-t border-gray-200",
                        submitButton:
                          "bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors",
                        resetButton:
                          "border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors",
                      },
                    }}
                    onSubmit={() => {}}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Consumer list panel */}
        <div className="md:col-span-2">
          <Card className="h-full pt-0">
            <CardContent className="h-full">
              <h3 className="text-base font-medium mb-4">Consumer List</h3>

              {consumers?.length === 0 ? (
                <div className="flex items-center justify-center h-64 border rounded-md bg-muted/20">
                  <p className="text-muted-foreground">
                    No consumers match your search
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {consumers?.map((consumer) => (
                    <div
                      key={consumer.id}
                      className={cn(
                        "border rounded-md p-4 cursor-pointer transition-all",
                        selectedConsumer?.id === consumer.id
                          ? "border-primary bg-primary/5"
                          : "hover:border-muted-foreground/20"
                      )}
                      onClick={() => handleSelectConsumer(consumer)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="card-heading-text">{consumer.name}</h4>
                          <div className="card-subheading-text">
                            Account: {consumer.accountNo} | Premise:{" "}
                            {consumer.premiseId}
                          </div>
                        </div>
                        <StatusBadge
                          status={
                            consumer.status === "ACTIVE" ? "success" : "warning"
                          }
                          text={consumer.status}
                        />
                      </div>

                      <Separator className="my-2" />

                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <div className="card-subheading-text">Utilities</div>
                          <div>{consumer.utilities.join(", ")}</div>
                        </div>
                        <div>
                          <div className="card-subheading-text">
                            Bill Amount:
                          </div>
                          <div>
                            {currency} {consumer.totalOutstanding}
                          </div>
                        </div>
                        <div>
                          <div className="card-subheading-text">Last Bill</div>
                          <div>{consumer.lastBillDate || "NA"}</div>
                        </div>
                        <div>
                          <div className="card-subheading-text">Activate Date</div>
                          <div>{consumer.activateDate || "NA"}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <Button
          onClick={handleDraft}
          disabled={!selectedConsumer || isSubmitting || isValidating}
          className="min-w-[200px] mr-5"
        >
          {isSubmitting || isValidating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {isSubmitting ? "Saving..." : "Validating..."}
            </>
          ) : (
            "Save As Draft"
          )}
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!selectedConsumer || isSubmitting || isValidating}
          className="min-w-[200px]"
        >
          {isSubmitting || isValidating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {isSubmitting ? "Submitting..." : "Validating..."}
            </>
          ) : (
            "Continue to Account Overview"
          )}
        </Button>
      </div>
    </div>
  );
};

export default Step1ConsumerSelection;
